/**
 * Les numéros de téléphone saisis dans le produit (#1344).
 *
 * Un champ libre laissait chaque écran inventer sa forme — avec espaces, sans, avec points — et
 * surtout ne disait **jamais de quel pays** venait le numéro. Un cabinet qui porte des consultants
 * en Belgique ou au Luxembourg stockait des numéros ambigus, et personne ne s'en apercevait avant
 * d'essayer d'appeler.
 *
 * La valeur reste **une seule chaîne** : c'est ce que l'API attend, et scinder le champ côté web
 * aurait obligé chaque appelant à recomposer — donc à le faire différemment quelque part.
 */

export type DialOption = {
  /** ISO 3166-1 alpha-2, pour la clé de liste et le drapeau. */
  code: string;
  dial: string;
  label: string;
  flag: string;
};

/**
 * Les indicatifs proposés.
 *
 * Volontairement courte : les pays où le portage salarial français recrute et facture, plus les
 * voisins frontaliers. Une liste de deux cents pays fait défiler pour rien — et « Autre » n'existe
 * pas ici, un indicatif absent se tape à la main dans le champ, qui accepte le `+`.
 */
export const DIAL_OPTIONS: DialOption[] = [
  { code: 'FR', dial: '+33', label: 'France', flag: '🇫🇷' },
  { code: 'BE', dial: '+32', label: 'Belgique', flag: '🇧🇪' },
  { code: 'CH', dial: '+41', label: 'Suisse', flag: '🇨🇭' },
  { code: 'LU', dial: '+352', label: 'Luxembourg', flag: '🇱🇺' },
  { code: 'MC', dial: '+377', label: 'Monaco', flag: '🇲🇨' },
  { code: 'ES', dial: '+34', label: 'Espagne', flag: '🇪🇸' },
  { code: 'IT', dial: '+39', label: 'Italie', flag: '🇮🇹' },
  { code: 'DE', dial: '+49', label: 'Allemagne', flag: '🇩🇪' },
  { code: 'PT', dial: '+351', label: 'Portugal', flag: '🇵🇹' },
  { code: 'GB', dial: '+44', label: 'Royaume-Uni', flag: '🇬🇧' },
  { code: 'NL', dial: '+31', label: 'Pays-Bas', flag: '🇳🇱' },
  { code: 'MA', dial: '+212', label: 'Maroc', flag: '🇲🇦' },
  { code: 'TN', dial: '+216', label: 'Tunisie', flag: '🇹🇳' },
  { code: 'SN', dial: '+221', label: 'Sénégal', flag: '🇸🇳' },
  { code: 'CI', dial: '+225', label: 'Côte d’Ivoire', flag: '🇨🇮' },
  { code: 'CA', dial: '+1', label: 'Canada / États-Unis', flag: '🇨🇦' },
];

export const DEFAULT_DIAL = '+33';

/**
 * Sépare un numéro enregistré en indicatif et partie nationale.
 *
 * **Un numéro sans `+` n'est pas réécrit** : il n'appartient à aucun indicatif connu, et lui en
 * attribuer un d'office transformerait `06 55 32 78 90` en `+33 6 55 32 78 90` dans une base où
 * personne n'a rien demandé. Il reste tel quel dans le champ national, sous l'indicatif par
 * défaut — et ne change que si quelqu'un le modifie.
 *
 * Les indicatifs sont essayés du plus long au plus court : `+33` avalerait le début de `+352`.
 */
export function splitPhone(value: string | null | undefined): { dial: string; national: string } {
  const raw = (value ?? '').trim();
  if (!raw.startsWith('+')) return { dial: DEFAULT_DIAL, national: raw };

  const compact = raw.replace(/[\s.\-()]/g, '');
  const match = [...DIAL_OPTIONS]
    .sort((a, b) => b.dial.length - a.dial.length)
    .find(option => compact.startsWith(option.dial));

  if (!match) return { dial: DEFAULT_DIAL, national: raw };
  return { dial: match.dial, national: compact.slice(match.dial.length) };
}

/**
 * Recompose la valeur enregistrée.
 *
 * **Vide reste vide** : un champ qu'on n'a pas rempli ne doit pas s'enregistrer « +33 ». Ce serait
 * un numéro de plus dans la base, faux, et impossible à distinguer d'un vrai à l'œil.
 */
export function joinPhone(dial: string, national: string): string {
  const digits = national.trim();
  if (!digits) return '';
  if (digits.startsWith('+')) return digits;
  return `${dial} ${digits}`.trim();
}

/**
 * Met en forme la saisie nationale.
 *
 * Par paires pour la France, où c'est l'usage, et par blocs de trois ailleurs — mais **seulement
 * ce qui est certain** : un zéro de tête est retiré sur les numéros français (`06…` sous `+33`
 * s'écrit `6…`), parce que le garder produit un numéro que personne ne peut composer depuis
 * l'étranger.
 */
export function formatNational(dial: string, input: string): string {
  const digits = input.replace(/\D/g, '');
  if (!digits) return '';

  if (dial === DEFAULT_DIAL) {
    // « 6 55 32 78 90 » : le chiffre de tête isolé, puis des paires — c'est ainsi qu'un numéro
    // français s'écrit une fois l'indicatif sorti, et le découper en paires dès le début donne
    // « 65 53 27 89 0 », que personne ne relit.
    const trimmed = digits.replace(/^0+/, '');
    const [first, ...rest] = trimmed;
    const pairs = rest.join('').match(/.{1,2}/g) ?? [];
    return [first, ...pairs].filter(Boolean).join(' ');
  }

  return (digits.match(/.{1,3}/g) ?? []).join(' ');
}

/** Le lien composable d'un numéro affiché — sans espaces, comme l'attend le téléphone. */
export function telHrefFor(value: string | null | undefined): string | null {
  const digits = (value ?? '').replace(/[^\d+]/g, '');
  return digits.replace(/\D/g, '').length >= 6 ? `tel:${digits}` : null;
}
